/**
 * DispatchBoard - Easy dispatch Kanban for the delivery whiteboard.
 * PIN-protected (Delivery PIN). Writes to the same delivery_whiteboards the
 * wall displays read from. Add jobs (auto-fill by PrintLogic # or manual),
 * tick off when delivered, and print a single-day run sheet.
 * Route: /dispatch
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Truck, Package, Plus, Check, X, Printer, Lock, Loader2, AlertTriangle,
  ChevronLeft, ChevronRight, MapPin, Search, RotateCcw, Home, ShoppingBag, Store, Hourglass
} from "lucide-react";
import { PICKUP_POINTS, isClickCollect, requestTypeLabel, formatWaitingSince } from "../delivery/requestTypes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const DAY_LABELS = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday" };

const mondayOf = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff)).toISOString().split("T")[0];
};
const addDays = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IE", { day: "numeric", month: "short" });

const EMPTY_FORM = {
  day: "", job_number: "", customer: "", location: "", pickup_point: "",
  request_type: "delivery", time_slot: "", boxes: "", driver: "", notes: "", requested_by: ""
};

const DispatchBoard = () => {
  const [pin, setPin] = useState("");
  const [verified, setVerified] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [checking, setChecking] = useState(false);

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [printDay, setPrintDay] = useState(null);

  const loadBoard = useCallback(async (ws, thePin) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/dispatch/board/${ws}?pin=${encodeURIComponent(thePin)}`);
      setBoard(res.data.whiteboard);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load the board.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (verified) loadBoard(weekStart, pin);
  }, [verified, weekStart, pin, loadBoard]);

  const verifyPin = async () => {
    setChecking(true);
    setPinError(false);
    try {
      const res = await axios.get(`${API}/delivery-request/verify-pin?pin=${encodeURIComponent(pin)}`);
      if (res.data.valid) setVerified(true);
      else setPinError(true);
    } catch {
      setPinError(true);
    } finally {
      setChecking(false);
    }
  };

  const openAdd = (day) => {
    setForm({ ...EMPTY_FORM, day: day || "" });
    setError("");
    setShowAdd(true);
  };

  const lookupJob = async () => {
    if (!form.job_number.trim()) return;
    setLookingUp(true);
    try {
      const res = await axios.post(`${API}/dispatch/lookup-job`, { pin, job_number: form.job_number.trim() });
      if (res.data.found) {
        setForm((f) => ({ ...f, customer: res.data.customer || f.customer }));
      } else {
        setError("No PrintLogic job found for that number — enter the customer manually.");
      }
    } catch {
      setError("Lookup failed — enter the customer manually.");
    } finally {
      setLookingUp(false);
    }
  };

  const saveJob = async () => {
    if (!form.day) { setError("Please pick a day."); return; }
    if (!form.customer.trim() && !form.job_number.trim()) { setError("Enter a customer or a job number."); return; }
    if (form.request_type === "click_collect" && !form.pickup_point) { setError("Please choose a pickup point."); return; }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/dispatch/board/${weekStart}/jobs`, { ...form, pin });
      setShowAdd(false);
      await loadBoard(weekStart, pin);
    } catch (err) {
      setError(err.response?.data?.message || "Could not add the delivery.");
    } finally {
      setSaving(false);
    }
  };

  const toggleDelivered = async (day, job) => {
    try {
      await axios.patch(`${API}/dispatch/board/${weekStart}/day/${day}/jobs/${job.job_id}/delivered`, {
        pin, delivered: !job.delivered
      });
      await loadBoard(weekStart, pin);
    } catch (err) {
      setError(err.response?.data?.message || "Could not update the job.");
    }
  };

  const removeJob = async (day, job) => {
    if (!window.confirm(`Remove ${job.customer || job.job_id} from ${DAY_LABELS[day]}?`)) return;
    try {
      await axios.delete(`${API}/dispatch/board/${weekStart}/day/${day}/jobs/${job.job_id}?pin=${encodeURIComponent(pin)}`);
      await loadBoard(weekStart, pin);
    } catch (err) {
      setError(err.response?.data?.message || "Could not remove the job.");
    }
  };

  const doPrint = (day) => {
    setPrintDay(day);
    setTimeout(() => { window.print(); }, 150);
  };

  // ---- PIN screen ----
  if (!verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-950 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md" data-testid="dispatch-pin-screen">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Dispatch Board</h1>
            <p className="text-gray-500 mt-2">Enter PIN to continue</p>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && verifyPin()}
              placeholder="Enter PIN"
              autoFocus
              data-testid="dispatch-pin-input"
              className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-lg text-gray-900 focus:outline-none transition-colors ${pinError ? "border-red-500 bg-red-50" : "border-gray-200 focus:border-blue-500"}`}
            />
          </div>
          {pinError && (
            <div className="flex items-center gap-2 text-red-600 text-sm mt-3" data-testid="dispatch-pin-error">
              <AlertTriangle className="w-4 h-4" /> Invalid PIN. Please try again.
            </div>
          )}
          <button
            onClick={verifyPin}
            disabled={checking || !pin}
            data-testid="dispatch-pin-submit"
            className="mt-5 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {checking ? <Loader2 className="w-5 h-5 animate-spin" /> : "Unlock"}
          </button>
        </div>
      </div>
    );
  }

  const printJobs = printDay && board ? (board.days?.[printDay]?.jobs || []) : [];
  const printGroups = (() => {
    const groups = {};
    printJobs.forEach((j) => {
      const key = isClickCollect(j) ? `Click & Collect – ${j.pickup_point || "Factory"}` : (j.location || "").trim() || "No location";
      (groups[key] = groups[key] || []).push(j);
    });
    return Object.keys(groups)
      .sort((a, b) => (a === "No location" ? 1 : b === "No location" ? -1 : a.localeCompare(b)))
      .map((k) => [k, groups[k]]);
  })();

  return (
    <div className="min-h-screen bg-gray-100" data-testid="dispatch-board">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-gray-300 shadow-sm px-4 py-3 print:hidden">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Truck className="w-7 h-7 text-blue-600" />
            <h1 className="text-xl font-black text-gray-800 tracking-tight">DISPATCH <span className="text-blue-600">BOARD</span></h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg" data-testid="dispatch-prev-week"><ChevronLeft className="w-5 h-5 text-gray-600" /></button>
            <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-200 text-sm font-bold text-gray-800 whitespace-nowrap">
              {fmtDate(weekStart)} – {fmtDate(addDays(weekStart, 4))}
            </div>
            <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg" data-testid="dispatch-next-week"><ChevronRight className="w-5 h-5 text-gray-600" /></button>
            <button onClick={() => setWeekStart(mondayOf(new Date()))} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-gray-600 flex items-center gap-1"><RotateCcw className="w-4 h-4" /> This week</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => openAdd("")} data-testid="dispatch-add-btn" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg flex items-center gap-2"><Plus className="w-5 h-5" /> Add Delivery</button>
            <a href="/hub" className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg" title="Back to Hub" data-testid="dispatch-hub-link"><Home className="w-5 h-5 text-gray-600" /></a>
          </div>
        </div>
      </header>

      {error && (
        <div className="max-w-7xl mx-auto px-4 pt-3 print:hidden">
          <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 px-4 py-2 rounded-lg text-sm" data-testid="dispatch-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        </div>
      )}

      {/* Board */}
      <div className="max-w-7xl mx-auto p-4 print:hidden">
        {loading && !board ? (
          <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {DAYS.map((day) => {
              const jobs = board?.days?.[day]?.jobs || [];
              const done = jobs.filter((j) => j.delivered).length;
              return (
                <div key={day} className="flex flex-col rounded-xl border border-gray-300 bg-white overflow-hidden" data-testid={`dispatch-col-${day}`}>
                  <div className="bg-slate-700 px-3 py-2 flex items-center justify-between">
                    <div>
                      <h2 className="text-sm font-black text-white uppercase tracking-wide">{DAY_LABELS[day]}</h2>
                      <p className="text-xs text-slate-300">{done}/{jobs.length} done</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => doPrint(day)} title="Print run sheet" data-testid={`dispatch-print-${day}`} className="p-1.5 bg-white/10 hover:bg-white/25 rounded text-white"><Printer className="w-4 h-4" /></button>
                      <button onClick={() => openAdd(day)} title="Add to this day" data-testid={`dispatch-add-${day}`} className="p-1.5 bg-white/10 hover:bg-white/25 rounded text-white"><Plus className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="flex-1 p-2 space-y-2 min-h-[120px]">
                    {jobs.length === 0 && (
                      <div className="text-center py-6 text-gray-400 text-xs"><Truck className="w-7 h-7 mx-auto mb-1 opacity-30" />No deliveries</div>
                    )}
                    {jobs.map((job) => {
                      const isCollection = (job.request_type || "").toLowerCase() === "collection";
                      const isCC = isClickCollect(job);
                      const hasNum = job.job_id && !String(job.job_id).toUpperCase().startsWith("REQ-");
                      const cardClass = job.delivered ? "bg-green-50 border-green-300" : isCC ? "bg-orange-50 border-orange-300 border-l-4" : "bg-gray-50 border-gray-200";
                      const tagClass = isCC ? "bg-orange-500 text-white" : isCollection ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700";
                      return (
                        <div key={job.job_id} className={`rounded-lg border p-2.5 ${cardClass}`} data-testid={`dispatch-job-${job.job_id}`}>
                          <div className="flex items-start justify-between gap-1">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className={`text-sm font-bold ${job.delivered ? "text-green-700" : isCC ? "text-orange-800" : "text-blue-700"}`}>{hasNum ? `#${job.job_id}` : "No Job #"}</span>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tagClass}`} data-testid={`dispatch-job-type-${job.job_id}`}>{requestTypeLabel(job.request_type)}</span>
                                {job.time_slot && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">{job.time_slot}</span>}
                              </div>
                              <p className="text-sm text-gray-800 font-medium break-words mt-0.5">{job.customer || "Customer"}</p>
                              {isCC && job.pickup_point && <p className="text-xs text-orange-900 font-bold flex items-start gap-1" data-testid={`dispatch-job-pickup-${job.job_id}`}><Store className="w-3.5 h-3.5 text-orange-600 flex-shrink-0 mt-0.5" />{job.pickup_point}</p>}
                              {job.location && <p className="text-xs text-gray-700 font-semibold flex items-start gap-1 break-words"><MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" /><span>{job.location}</span></p>}
                              {job.boxes && <p className="text-xs text-gray-500">{job.boxes} box{job.boxes === "1" ? "" : "es"}</p>}
                              {(job.notes || job.product) && <p className="text-xs text-gray-500 break-words whitespace-pre-line" data-testid={`dispatch-job-notes-${job.job_id}`}>{job.notes || job.product}</p>}
                              {job.driver && <p className="text-[11px] text-gray-400 break-words">Driver: {job.driver}</p>}
                              {isCC && !job.delivered && job.carried_from && (
                                <p className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`dispatch-job-waiting-${job.job_id}`}><Hourglass className="w-3 h-3" /> Waiting since {formatWaitingSince(job.carried_from)}</p>
                              )}
                            </div>
                            <button onClick={() => removeJob(day, job)} title="Remove" data-testid={`dispatch-remove-${job.job_id}`} className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0"><X className="w-4 h-4" /></button>
                          </div>
                          <button
                            onClick={() => toggleDelivered(day, job)}
                            data-testid={`dispatch-delivered-${job.job_id}`}
                            className={`mt-2 w-full py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-colors ${job.delivered ? "bg-green-600 hover:bg-green-700 text-white" : isCC ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                          >
                            <Check className="w-4 h-4" /> {job.delivered ? (isCC ? "Collected" : "Delivered") : (isCC ? "Mark Collected" : "Mark Delivered")}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 print:hidden" data-testid="dispatch-add-modal">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Add Delivery / Collection / Click &amp; Collect</h3>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-700" data-testid="dispatch-add-close"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setForm((f) => ({ ...f, request_type: "delivery" }))} data-testid="dispatch-type-delivery" className={`py-2.5 px-1 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-1.5 ${form.request_type === "delivery" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-500"}`}><Truck className="w-4 h-4 flex-shrink-0" /> Delivery</button>
                <button onClick={() => setForm((f) => ({ ...f, request_type: "collection" }))} data-testid="dispatch-type-collection" className={`py-2.5 px-1 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-1.5 ${form.request_type === "collection" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-500"}`}><Package className="w-4 h-4 flex-shrink-0" /> Collection</button>
                <button onClick={() => setForm((f) => ({ ...f, request_type: "click_collect" }))} data-testid="dispatch-type-click-collect" className={`py-2.5 px-1 rounded-xl font-bold text-sm border-2 flex items-center justify-center gap-1.5 ${form.request_type === "click_collect" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-500"}`}><ShoppingBag className="w-4 h-4 flex-shrink-0" /> <span className="leading-tight text-left">Click &amp; Collect</span></button>
              </div>

              {form.request_type === "click_collect" && (
                <div data-testid="dispatch-pickup-field">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Factory Pickup Point <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-2 gap-2">
                    {PICKUP_POINTS.map((pp) => (
                      <button key={pp} type="button" onClick={() => setForm((f) => ({ ...f, pickup_point: pp }))} data-testid={`dispatch-pickup-${pp.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-semibold text-left ${form.pickup_point === pp ? "border-orange-500 bg-orange-50 text-orange-800" : "border-gray-200 text-gray-600 hover:border-orange-300"}`}>
                        <Store className={`w-4 h-4 flex-shrink-0 ${form.pickup_point === pp ? "text-orange-600" : "text-gray-400"}`} />{pp}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day <span className="text-red-500">*</span></label>
                <select value={form.day} onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))} data-testid="dispatch-form-day" className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white text-gray-900">
                  <option value="">Select a day…</option>
                  {DAYS.map((d) => <option key={d} value={d}>{DAY_LABELS[d]}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Number <span className="text-gray-400">(optional — auto-fills customer)</span></label>
                <div className="flex gap-2">
                  <input value={form.job_number} onChange={(e) => setForm((f) => ({ ...f, job_number: e.target.value }))} placeholder="e.g. 68177" data-testid="dispatch-form-jobnumber" className="flex-1 px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-gray-900" />
                  <button onClick={lookupJob} disabled={lookingUp || !form.job_number.trim()} data-testid="dispatch-form-lookup" className="px-4 rounded-xl bg-slate-700 hover:bg-slate-800 disabled:bg-gray-300 text-white font-semibold flex items-center gap-1">{lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Look up</button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer <span className="text-red-500">*</span></label>
                <input value={form.customer} onChange={(e) => setForm((f) => ({ ...f, customer: e.target.value }))} placeholder="Customer name" data-testid="dispatch-form-customer" className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-gray-900" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {form.request_type !== "click_collect" ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="e.g. Santry" data-testid="dispatch-form-location" className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-gray-900" />
                </div>
                ) : <div />}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <select value={form.time_slot} onChange={(e) => setForm((f) => ({ ...f, time_slot: e.target.value }))} data-testid="dispatch-form-time" className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white text-gray-900">
                    <option value="">Any time</option>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Boxes</label>
                  <input value={form.boxes} onChange={(e) => setForm((f) => ({ ...f, boxes: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="e.g. 3" data-testid="dispatch-form-boxes" className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
                  <input value={form.driver} onChange={(e) => setForm((f) => ({ ...f, driver: e.target.value }))} placeholder="Driver / courier" data-testid="dispatch-form-driver" className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-gray-900" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Any special instructions…" data-testid="dispatch-form-notes" className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none resize-none text-gray-900" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
                <input value={form.requested_by} onChange={(e) => setForm((f) => ({ ...f, requested_by: e.target.value }))} placeholder="Who's adding this?" data-testid="dispatch-form-by" className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-gray-900" />
              </div>

              {error && (
                <p className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg text-sm" data-testid="dispatch-modal-error">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
                </p>
              )}

              <button onClick={saveJob} disabled={saving} data-testid="dispatch-form-save" className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-bold flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Add to Board
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Printable run sheet (single day) */}
      <div className="hidden print:block p-6" data-testid="dispatch-print-area">
        {printDay && (
          <>
            <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-4">
              <div>
                <h1 className="text-2xl font-black">Dispatch Run Sheet</h1>
                <p className="text-sm">{DAY_LABELS[printDay]} · {fmtDate(addDays(weekStart, DAYS.indexOf(printDay)))}</p>
              </div>
              <p className="text-sm">Printed {new Date().toLocaleString("en-IE")}</p>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-black text-left">
                  <th className="py-1 pr-2">✓</th>
                  <th className="py-1 pr-2">Job #</th>
                  <th className="py-1 pr-2">Customer</th>
                  <th className="py-1 pr-2">Type</th>
                  <th className="py-1 pr-2">Time</th>
                  <th className="py-1 pr-2">Boxes</th>
                  <th className="py-1 pr-2">Driver</th>
                  <th className="py-1 pr-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {printGroups.map(([loc, jobs]) => (
                  [
                    <tr key={`loc-${loc}`} className="border-b border-black bg-gray-200">
                      <td colSpan={8} className="py-1.5 px-1 font-black uppercase tracking-wide">📍 {loc} ({jobs.length})</td>
                    </tr>,
                    ...jobs.map((j) => (
                      <tr key={`${loc}-${j.job_id}`} className="border-b border-gray-400 align-top">
                        <td className="py-1.5 pr-2">{j.delivered ? "☑" : "☐"}</td>
                        <td className="py-1.5 pr-2">{String(j.job_id).toUpperCase().startsWith("REQ-") ? "—" : j.job_id}</td>
                        <td className="py-1.5 pr-2 font-semibold">{j.customer}</td>
                        <td className="py-1.5 pr-2">{requestTypeLabel(j.request_type)}{isClickCollect(j) && j.pickup_point ? ` – ${j.pickup_point}` : ""}</td>
                        <td className="py-1.5 pr-2">{j.time_slot || ""}</td>
                        <td className="py-1.5 pr-2">{j.boxes || ""}</td>
                        <td className="py-1.5 pr-2">{j.driver || ""}</td>
                        <td className="py-1.5 pr-2">{j.notes || j.product || ""}</td>
                      </tr>
                    ))
                  ]
                ))}
                {printJobs.length === 0 && (
                  <tr><td colSpan={8} className="py-3 text-center">No deliveries for this day.</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default DispatchBoard;
